import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Same real bug as Payment.tsx's fix (2026-08-14, see util/stripe.ts's
// comment) - loadStripe() called per-render breaks the Elements context
// silently. Imports the one shared instance instead of creating its own.
import stripePromise from "../../util/stripe";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import CommonButton from "../utilities/formElements/commonButton/CommonButton";
import {
  toastMessageError,
  toastMessageSuccess,
} from "../utilities/commonToastMessage";

import {
  CreditBlock,
  getCreditBlocks,
  purchaseCredits,
} from "../../util/creditsModel";
import { subscription } from "../../services/susbscription";
import ActionType from "../../resources/enums";
import "./CreditPurchaseModal.scss";

interface CreditPurchaseModalProps {
  onClose: () => void;
  // Real bug reported + fixed 2026-08-14: refreshAccountRecord() below
  // already correctly re-dispatched SUBSCRIPTIONDATA after a purchase, so
  // the CREDIT BALANCE and effective page-view LIMIT (both derived from
  // authReducer.accountRecord) updated fine - but Settings.tsx's actual
  // page-views-USED count is separate local component state
  // (pageViewsUsed), fetched once in a mount-only effect
  // (getSubscriptionPageViewCount, deps: []) with no way to know a
  // purchase just happened elsewhere. Net effect: the limit changed, the
  // used count didn't, and the usage bar/percent looked stale or
  // inconsistent until a full reload re-mounted the page. onSuccess is
  // optional so this component doesn't require a parent that cares (only
  // Settings.tsx's usage-bar page does).
  onSuccess?: () => void;
}

// The actual purchase step - split out from the wrapper below because
// useStripe()/useElements() only work INSIDE an <Elements> provider, and
// that provider is only needed at all for the fallback card-entry path
// (see the wrapper's own comment).
const CreditPurchaseForm: React.FC<CreditPurchaseModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { t: translation } = useTranslation();
  const dispatch = useDispatch();
  const authReducer = useSelector((state: any) => state?.AuthReducer);
  const stripe = useStripe();
  const elements = useElements();

  const [blocks, setBlocks] = useState<CreditBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  // Set only when purchaseCredits comes back with no saved card on file -
  // the one path that needs real card entry, everything else (off-session
  // charge succeeded, or a 3D Secure follow-up) never shows this.
  const [needsNewCard, setNeedsNewCard] = useState(false);
  const [pendingClientSecret, setPendingClientSecret] = useState<
    string | null
  >(null);

  useEffect(() => {
    getCreditBlocks().then(setBlocks);
  }, []);

  const fetchAccountRecord = async () => {
    const data = await subscription({
      jsonprc: "2.0",
      method: "getAccountRecord",
      params: { userId: authReducer.authData._id },
      id: 0,
    });
    return data.data?.result;
  };

  const refreshAccountRecord = async () => {
    const record = await fetchAccountRecord();
    dispatch({
      type: ActionType.SUBSCRIPTIONDATA,
      payload: record,
    });
  };

  // Real bug found 2026-08-14 (staging, live purchase): applyCreditPurchase
  // only ever runs from WebhookService's payment_intent.succeeded handler,
  // never synchronously from purchaseCredits() itself. Stripe confirms the
  // charge to the CLIENT well before its webhook reaches OUR backend, so
  // calling refreshAccountRecord() immediately here was a genuine race -
  // it routinely fetched the account record from BEFORE the webhook had
  // applied the credit, and nothing ever refetched afterward. Balance was
  // always correct server-side (confirmed directly in Mongo) - the UI just
  // permanently displayed a pre-webhook snapshot, surviving even a hard
  // reload since that reload just re-ran the same too-early-relative-to-
  // webhook fetch pattern (no - reload actually does eventually see it once
  // the webhook lands; the real symptom was THIS immediate refresh call
  // freezing stale data into Redux/persisted storage before the webhook
  // ever got a chance to land). Fixed by polling for the balance to
  // actually increase (bounded: 10 tries, 800ms apart, ~8s) before treating
  // the purchase as reflected - falls back to one final dispatch either way
  // so the flow still closes even if the webhook is unusually slow.
  const waitForBalanceIncrease = async (
    previousBalance: number,
    attempts = 10,
    delayMs = 800
  ) => {
    for (let i = 0; i < attempts; i++) {
      const record = await fetchAccountRecord();
      const newBalance = record?.bonusPageViewCredits?.balance ?? 0;
      if (newBalance > previousBalance) {
        dispatch({ type: ActionType.SUBSCRIPTIONDATA, payload: record });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    // Webhook still hasn't landed after ~8s - dispatch whatever we last
    // fetched anyway rather than leaving the UI stuck on the pre-purchase
    // value indefinitely with no further retry.
    await refreshAccountRecord();
  };

  const finishSuccessfully = async () => {
    const previousBalance =
      authReducer?.accountRecord?.bonusPageViewCredits?.balance ?? 0;
    toastMessageSuccess(
      translation("credits_purchased_successfully"),
      "creditsPurchased"
    );
    onClose();
    await waitForBalanceIncrease(previousBalance);
    onSuccess?.();
  };

  const handleBuy = async () => {
    if (!selectedBlockId) {
      return;
    }

    setLoading(true);
    try {
      const result = await purchaseCredits(
        authReducer.authData._id,
        selectedBlockId
      );

      if (!result.clientSecret) {
        // Off-session charge on the saved card succeeded synchronously -
        // nothing further to do.
        await finishSuccessfully();
        return;
      }

      if (result.hasSavedPaymentMethod) {
        // Saved card exists but needs a 3D Secure/SCA challenge completed -
        // no new card details needed, just the challenge itself.
        if (!stripe) {
          throw new Error("Stripe.js not ready");
        }
        const { error } = await stripe.handleCardAction(result.clientSecret);
        if (error) {
          throw new Error(error.message);
        }
        await finishSuccessfully();
        return;
      }

      // No saved card at all - fall through to real card entry below.
      setPendingClientSecret(result.clientSecret);
      setNeedsNewCard(true);
    } catch (error: any) {
      toastMessageError(
        error?.message || translation("something_went_wrong"),
        "creditsPurchaseError"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNewCard = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !pendingClientSecret) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await stripe.confirmCardPayment(
        pendingClientSecret,
        { payment_method: { card: cardElement } }
      );
      if (error) {
        throw new Error(error.message);
      }
      await finishSuccessfully();
    } catch (error: any) {
      toastMessageError(
        error?.message || translation("something_went_wrong"),
        "creditsPurchaseError"
      );
    } finally {
      setLoading(false);
    }
  };

  if (needsNewCard) {
    return (
      <form onSubmit={handleConfirmNewCard} className="credit-purchase-card-form">
        <p>{translation("no_saved_payment_method_add_one_to_continue")}</p>
        <div className="stripe-card-element-wrapper">
          <CardElement options={{ hidePostalCode: false }} />
        </div>
        <div className="credit-purchase-actions">
          <CommonButton
            type="button"
            label={translation("back")}
            className="theme-btn transparent-btn"
            onClick={() => setNeedsNewCard(false)}
            disabled={loading}
          />
          <CommonButton
            type="submit"
            label={loading ? translation("processing") : translation("pay_and_add_credits")}
            className="theme-btn success-btn"
            disabled={loading || !stripe}
          />
        </div>
      </form>
    );
  }

  return (
    <div className="credit-purchase-blocks">
      {blocks.map((block) => (
        <label
          key={block.id}
          className={
            selectedBlockId === block.id
              ? "credit-block-option selected"
              : "credit-block-option"
          }
        >
          <input
            type="radio"
            name="creditBlock"
            value={block.id}
            checked={selectedBlockId === block.id}
            onChange={() => setSelectedBlockId(block.id)}
          />
          <span className="credit-block-views">
            {block.pageViews.toLocaleString()} {translation("page_views")}
          </span>
          <span className="credit-block-price">
            ${(block.priceCents / 100).toFixed(2)}
          </span>
        </label>
      ))}
      <div className="credit-purchase-actions">
        <CommonButton
          type="button"
          label={loading ? translation("processing") : translation("buy_credits")}
          className="theme-btn success-btn"
          onClick={handleBuy}
          disabled={loading || !selectedBlockId}
        />
      </div>
    </div>
  );
};

// Wraps the form in <Elements> unconditionally (matching Payment.tsx's own
// pattern) even though most purchases never render a single Stripe
// Element - useStripe()/useElements() still need the provider present to
// resolve stripe.handleCardAction for the 3D-Secure-only path, and
// <Elements> itself is cheap or provider isn't - no separate lazy-mount
// needed.
const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { t: translation } = useTranslation();
  const authReducer = useSelector((state: any) => state?.AuthReducer);

  const balance =
    authReducer?.accountRecord?.bonusPageViewCredits?.balance ?? 0;

  return (
    <div className="modal show-modal common-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{translation("buy_more_page_views")}</h4>
            <CommonButton
              type="button"
              className="btnC btn-close btn-close-white"
              onClick={onClose}
              data-bs-dismiss="modal"
            />
          </div>
          <div className="modal-body common-body text-primary">
            {balance > 0 && (
              <p className="credit-current-balance">
                {translation("current_credit_balance")}:{" "}
                {balance.toLocaleString()} {translation("page_views")}
              </p>
            )}
            <Elements stripe={stripePromise}>
              <CreditPurchaseForm onClose={onClose} onSuccess={onSuccess} />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;
